"use client";

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Star, Heart, Sun, Wind, Volume2 } from 'lucide-react';
import { AboutLayout } from '@/components/about/about-layout';

export default function JudgementCardPage() {
  return (
    <AboutLayout activeSection="tarot">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Current Section Indicator */}
        <div className="mb-4 p-3 bg-violet-500/10 border border-violet-500/20 rounded-lg">
          <div className="flex items-center gap-2 text-violet-300">
            <Star className="h-4 w-4" />
            <span className="text-sm font-medium">🃏 Tarot</span>
            <span className="text-violet-500">/</span>
            <span className="text-sm">Rider-Waite</span>
            <span className="text-violet-500">/</span>
            <span className="text-sm">Majör Arkana</span>
            <span className="text-violet-500">/</span>
            <span className="text-sm">Judgement</span>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="mb-6">
          <Link
            href="/about"
            className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            AXTROLOG Nedir
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <Link
            href="/about/tarot/systems/rider-waite"
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            Rider-Waite
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <Link
            href="/about/tarot/systems/rider-waite/major-arcana"
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            Majör Arkana
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <span className="text-slate-300">Judgement</span>
        </div>

        {/* Header with Card */}
        <div className="bg-gradient-to-r from-violet-900/20 to-purple-900/20 rounded-xl p-8 mb-8 border border-violet-500/20">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
            <div className="lg:col-span-1 flex justify-center">
              <div className="w-48 h-72 rounded-xl overflow-hidden shadow-2xl">
                <img
                  src="/images/tarot/cards/rider-waite-judgement.svg"
                  alt="Judgement Tarot Card"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-purple-700 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-lg">XX</span>
                </div>
                <div>
                  <h1 className="text-4xl font-bold text-slate-100">Judgement</h1>
                  <p className="text-xl text-violet-400">Mahkeme / Yeniden Doğuş</p>
                </div>
              </div>
              <p className="text-lg text-slate-200 leading-relaxed mb-4">
                Yeniden doğuş, ruhsal uyanış ve geçmişin değerlendirilmesinin kartı.
                Divine çağrı, bağışlama ve yeni bir yaşam döngüsünün başlangıcını temsil eder.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-violet-500/20 text-violet-300 rounded-full text-sm">Yeniden doğuş</span>
                <span className="px-3 py-1 bg-violet-500/20 text-violet-300 rounded-full text-sm">Uyanış</span>
                <span className="px-3 py-1 bg-violet-500/20 text-violet-300 rounded-full text-sm">Bağışlama</span>
                <span className="px-3 py-1 bg-violet-500/20 text-violet-300 rounded-full text-sm">Divine çağrı</span>
              </div>
            </div>
          </div>
        </div>

        {/* Card Meanings */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Positive Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Sun className="h-6 w-6 text-green-400" />
              Düz Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Ruhsal Uyanış</h3>
                  <p className="text-slate-400 text-sm">Yüksek bilinç, aydınlanma ve divine bağlantı.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Yeniden Doğuş</h3>
                  <p className="text-slate-400 text-sm">Kökten değişim, yeni bir yaşam başlangıcı.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Bağışlama</h3>
                  <p className="text-slate-400 text-sm">Geçmişi serbest bırakma, kendini ve başkalarını affetme.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">İçsel Çağrı</h3>
                  <p className="text-slate-400 text-sm">Yaşam amacını keşfetme, misyon bilinci.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Negative Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Wind className="h-6 w-6 text-red-400" />
              Ters Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">İçsel Çağrıyı Görmezden Gelme</h3>
                  <p className="text-slate-400 text-sm">Ruhsal mesajları duymazdan gelme, direniş.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Geçmişe Takılma</h3>
                  <p className="text-slate-400 text-sm">Eski yaralara sarılma, bağışlayamama.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Kendini Yargılama</h3>
                  <p className="text-slate-400 text-sm">Aşırı eleştiri, suçluluk ve utanç duyguları.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Değişime Direnç</h3>
                  <p className="text-slate-400 text-sm">Dönüşümden korkma, eski kalıplarda ısrar.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Symbolism */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Sembolik Anlamlar</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">👼</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Baş Melek Gabriel</h3>
              <p className="text-slate-400 text-sm">Divine mesajcı, ruhsal rehberlik ve müjde.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-yellow-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <Volume2 className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Trompet</h3>
              <p className="text-slate-400 text-sm">Divine çağrı, uyanış sesi ve ruhsal mesaj.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-gray-800 text-lg">⚰️</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Dirilme</h3>
              <p className="text-slate-400 text-sm">Ölümden sonra yaşam, yeniden doğuş ve dönüşüm.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">🏴</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Kırmızı Haç</h3>
              <p className="text-slate-400 text-sm">Ruhsal kurtuluş, fedakarlık ve divine aşk.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">🌊</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Su</h3>
              <p className="text-slate-400 text-sm">Arınma, yenilenme ve ruhsal temizlik.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">⛰️</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Dağlar</h3>
              <p className="text-slate-400 text-sm">Ruhsal yükseliş, aştığınız zorluklar ve bilgelik.</p>
            </div>
          </div>
        </div>

        {/* Life Areas */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Yaşam Alanlarındaki Anlamlar</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Heart className="h-5 w-5 text-pink-400" />
                Aşk ve İlişkiler
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• İlişkinin yeniden doğuşu</li>
                <li>• Bağışlama ve uzlaşma</li>
                <li>• Ruhsal bağlantı</li>
                <li>• Karmik ilişkilerin tamamlanması</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Star className="h-5 w-5 text-yellow-400" />
                Kariyer ve İş
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Yeni bir kariyer çağrısı</li>
                <li>• Misyon temelli çalışma</li>
                <li>• Geçmiş deneyimlerin değerlendirilmesi</li>
                <li>• Ruhsal tatmin veren iş</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Sun className="h-5 w-5 text-green-400" />
                Kişisel Gelişim
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Ruhsal uyanış</li>
                <li>• Yaşam amacını bulma</li>
                <li>• Geçmişle barışma</li>
                <li>• İçsel yargıcı sakinleştirme</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Wind className="h-5 w-5 text-blue-400" />
                Sağlık ve İyileşme
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Derin iyileşme süreci</li>
                <li>• Holistik sağlık yaklaşımı</li>
                <li>• Mental ve ruhsal arınma</li>
                <li>• Yenilenme ve canlanma</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Important Note */}
        <div className="bg-amber-900/20 border border-amber-700/50 rounded-xl p-6 mb-8">
          <h3 className="text-lg font-semibold text-amber-200 mb-3">⚠️ Önemli Uyarı</h3>
          <p className="text-amber-100 leading-relaxed">
            Bu kart anlamları geleneksel tarot yorumlarına dayanmaktadır ve sadece eğlence ve kişisel farkındalık amaçlıdır.
            Yaşamsal kararlar için uzman görüşü alınmalıdır.
          </p>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Link
            href="/about/tarot/systems/rider-waite/major-arcana/sun"
            className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            The Sun
          </Link>

          <Link
            href="/about/tarot/systems/rider-waite/major-arcana/world"
            className="inline-flex items-center px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg transition-colors"
          >
            The World
            <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
          </Link>
        </div>
      </div>
    </AboutLayout>
  );
}